import { Dialog } from '@progress/kendo-react-dialogs';
import styles from './KeyboardShortcutsDialog.module.css';

interface ShortcutEntry {
  keys: string[];
  description: string;
  context?: string;
}

interface ShortcutGroup {
  title: string;
  entries: ShortcutEntry[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    entries: [
      { keys: ['/'], description: 'Focus the document search' },
      { keys: ['Esc'], description: 'Clear the search query', context: 'Search box focused' },
      { keys: ['↑', '↓'], description: 'Move between tree items' },
      { keys: ['→'], description: 'Expand the focused folder' },
      { keys: ['←'], description: 'Collapse the focused folder' },
      { keys: ['Enter'], description: 'Open the focused item' },
    ],
  },
  {
    title: 'Menus',
    entries: [
      { keys: ['m'], description: 'Open the “more actions” menu' },
      { keys: ['c'], description: 'Open the right-click context menu' },
    ],
  },
  {
    title: 'Direct actions on the selected item',
    entries: [
      { keys: ['n'], description: 'New folder', context: 'Folder + admin' },
      { keys: ['u'], description: 'Upload file', context: 'Folder + admin' },
      { keys: ['r'], description: 'Rename', context: 'Item + admin' },
      { keys: ['d'], description: 'Delete (asks for confirmation)', context: 'Item + admin' },
      { keys: ['o'], description: 'Delete folder content (asks for confirmation)', context: 'Folder + admin' },
      { keys: ['s'], description: 'Share', context: 'Item + admin' },
      { keys: ['w'], description: 'Download', context: 'Document only' },
      { keys: ['z'], description: 'Download as ZIP', context: 'Folder only' },
    ],
  },
  {
    title: 'Dialogs',
    entries: [
      { keys: ['Enter'], description: 'Confirm / submit the dialog' },
      { keys: ['Esc'], description: 'Close the active dialog' },
    ],
  },
  {
    title: 'Help',
    entries: [{ keys: ['?'], description: 'Show this shortcuts dialog' }],
  },
];

interface KeyboardShortcutsDialogProps {
  onClose: () => void;
}

const KeyboardShortcutsDialog = ({ onClose }: KeyboardShortcutsDialogProps): React.ReactElement => (
  <Dialog title="Keyboard shortcuts" onClose={onClose} width={720}>
    <div className={styles['shortcuts-content']}>
      <p className={styles['shortcuts-intro']}>
        Speed up navigation with these keyboard shortcuts. Most apply on the documents page.
      </p>
      <div className={styles['shortcuts-grid']}>
        {SHORTCUT_GROUPS.map((group) => (
          <section key={group.title} className={styles['shortcuts-group']}>
            <h3 className={styles['shortcuts-group-title']}>{group.title}</h3>
            <table className={styles['shortcuts-table']}>
              <tbody>
                {group.entries.map((shortcut, idx) => (
                  <tr key={idx}>
                    <td className={styles['shortcuts-keys']}>
                      {shortcut.keys.map((k, i) => (
                        <span key={i}>
                          <kbd className={styles['shortcuts-kbd']}>{k}</kbd>
                          {i < shortcut.keys.length - 1 && (
                            <span className={styles['shortcuts-or']}>or</span>
                          )}
                        </span>
                      ))}
                    </td>
                    <td className={styles['shortcuts-description']}>
                      {shortcut.description}
                      {shortcut.context && (
                        <span className={styles['shortcuts-context']}> · {shortcut.context}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  </Dialog>
);

export default KeyboardShortcutsDialog;
